import productoService from '../services/productoService';
import type { PreciosProveedorImportResult } from '../services/preciosProveedorService';
import {
  ExcelFilaPreciosImport,
  parseFilaDesdeErrorFila,
  roundPrecioImport,
} from './preciosImportExcelParse';

export type PreciosImportPreviewEstado =
  | 'actualizar'
  | 'no_encontrado'
  | 'invalido'
  | 'sin_datos';

export interface PreciosImportPreviewProductoMeta {
  calculaIva?: string;
  ivaCF?: number | null;
  porcentajeGanan?: number | null;
}

export interface PreciosImportPreviewRow {
  filaExcel: number;
  codigo: string;
  descripcion?: string;
  costoActual?: number | null;
  costoNuevo?: number | null;
  precioNetoActual?: number | null;
  precioNetoNuevo?: number | null;
  precioFinalActual?: number | null;
  precioFinalNuevo?: number | null;
  precioMinimoActual?: number | null;
  precioMinimoNuevo?: number | null;
  estado: PreciosImportPreviewEstado;
  mensaje?: string;
  /** Valores originales del Excel / simulación inicial. */
  costoNuevoOriginal?: number | null;
  precioNetoNuevoOriginal?: number | null;
  precioMinimoNuevoOriginal?: number | null;
  /** Metadatos del artículo para recalcular precios al editar. */
  productoMeta?: PreciosImportPreviewProductoMeta | null;
  /** El usuario modificó costo o precio neto respecto al original. */
  editado?: boolean;
  /** Precio neto ingresado manualmente (tiene prioridad sobre recálculo por costo). */
  precioNetoManual?: boolean;
}

/**
 * Cache de productos por código para reutilizar entre invocaciones consecutivas de
 * `buildPreciosImportPreviewArticulo` (p. ej. simular varias veces sobre el mismo archivo).
 * El caller es responsable de invalidarlo tras una ejecución real (no dry-run).
 */
export type ProductoPreviewCache = Map<string, Record<string, unknown> | null>;

export function crearProductoPreviewCache(): ProductoPreviewCache {
  return new Map();
}

function calcPrecioFinal(precioNeto: number, producto: Record<string, unknown>): number {
  return calcPrecioFinalMeta(precioNeto, metaDesdeProducto(producto));
}

function metaDesdeProducto(producto: Record<string, unknown>): PreciosImportPreviewProductoMeta {
  return {
    calculaIva: producto.calculaIva != null ? String(producto.calculaIva) : undefined,
    ivaCF: numOrNull(producto.ivaCF),
    porcentajeGanan: numOrNull(producto.porcentajeGanan),
  };
}

export function calcPrecioFinalMeta(
  precioNeto: number,
  meta: PreciosImportPreviewProductoMeta
): number {
  if (meta.calculaIva === 'S') {
    const pct = meta.ivaCF ?? 0;
    return roundPrecioImport(precioNeto * (1 + pct / 100));
  }
  return roundPrecioImport(precioNeto);
}

export function estimarPrecioNetoDesdeCostoMeta(
  costo: number,
  meta: PreciosImportPreviewProductoMeta
): number | null {
  const pct = meta.porcentajeGanan ?? 0;
  if (!Number.isFinite(pct)) return null;
  return roundPrecioImport(costo * (1 + pct / 100));
}

function numOrNull(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? roundPrecioImport(n) : null;
}

async function mapInBatches<T, R>(
  items: T[],
  batchSize: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const out: R[] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const results = await Promise.all(batch.map(fn));
    out.push(...results);
  }
  return out;
}

function buildErroresPorFila(erroresFila: string[]): Map<number, string> {
  const map = new Map<number, string>();
  for (const err of erroresFila) {
    const fila = parseFilaDesdeErrorFila(err);
    if (fila != null) map.set(fila, err);
  }
  return map;
}

function buildNoMatchPorFila(
  sinCoincidenciaDetalle: PreciosProveedorImportResult['sinCoincidenciaDetalle'],
  modo: 'articulo' | 'proveedor'
): Map<number, string> {
  const map = new Map<number, string>();
  for (const d of sinCoincidenciaDetalle ?? []) {
    const etiqueta =
      modo === 'articulo'
        ? d.codigoArticulo ?? d.codigoArticuloProveedor ?? ''
        : d.codigoArticuloProveedor ?? d.codigoArticulo ?? '';
    map.set(d.filaExcel, etiqueta);
  }
  return map;
}

function resolverPrecioNetoNuevo(
  fila: ExcelFilaPreciosImport,
  producto: Record<string, unknown> | null
): number | null {
  if (fila.precioNetoExcel !== null && fila.precioNetoExcel !== undefined) {
    return fila.precioNetoExcel;
  }
  if (fila.costoExcel !== null && fila.costoExcel !== undefined && producto) {
    return estimarPrecioNetoDesdeCostoMeta(fila.costoExcel, metaDesdeProducto(producto));
  }
  return null;
}

/** Concurrencia de lecturas simultáneas de `getProducto`. */
const PRODUCTO_FETCH_CONCURRENCY = 16;

function filaTieneDatosExcel(fila: ExcelFilaPreciosImport): boolean {
  return (
    (fila.costoExcel !== null && fila.costoExcel !== undefined) ||
    (fila.precioNetoExcel !== null && fila.precioNetoExcel !== undefined) ||
    (fila.precioMinimoExcel !== null && fila.precioMinimoExcel !== undefined)
  );
}

async function precargarProductosEnCache(
  codigos: string[],
  cache: ProductoPreviewCache
): Promise<void> {
  const pendientes = Array.from(new Set(codigos)).filter((c) => c && !cache.has(c));
  if (pendientes.length === 0) return;

  await mapInBatches(pendientes, PRODUCTO_FETCH_CONCURRENCY, async (codigo) => {
    try {
      const p = (await productoService.getProducto(codigo)) as unknown as Record<string, unknown>;
      cache.set(codigo, p);
    } catch {
      cache.set(codigo, null);
    }
  });
}

export interface BuildPreciosImportPreviewArticuloOptions {
  /**
   * Cache opcional de productos por código. Si se provee, se reutiliza y se enriquece
   * con los códigos consultados en esta invocación.
   */
  productoCache?: ProductoPreviewCache;
}

export async function buildPreciosImportPreviewArticulo(
  filas: ExcelFilaPreciosImport[],
  resultadoApi?: PreciosProveedorImportResult | null,
  options?: BuildPreciosImportPreviewArticuloOptions
): Promise<PreciosImportPreviewRow[]> {
  const erroresPorFila = buildErroresPorFila(resultadoApi?.erroresFila ?? []);
  const noMatchPorFila = buildNoMatchPorFila(resultadoApi?.sinCoincidenciaDetalle ?? [], 'articulo');
  const cache = options?.productoCache ?? crearProductoPreviewCache();

  const codigosAConsultar = filas
    .filter(
      (fila) =>
        !erroresPorFila.has(fila.filaExcel) &&
        !!fila.codigo &&
        filaTieneDatosExcel(fila) &&
        !noMatchPorFila.has(fila.filaExcel)
    )
    .map((fila) => fila.codigo);

  await precargarProductosEnCache(codigosAConsultar, cache);

  return filas.map((fila) => {
    const errorMsg = erroresPorFila.get(fila.filaExcel);
    if (errorMsg) {
      return {
        filaExcel: fila.filaExcel,
        codigo: fila.codigo,
        estado: 'invalido' as const,
        mensaje: errorMsg,
      };
    }

    if (!fila.codigo) {
      return {
        filaExcel: fila.filaExcel,
        codigo: '',
        estado: 'invalido' as const,
        mensaje: 'Código de artículo vacío',
      };
    }

    if (!filaTieneDatosExcel(fila)) {
      return {
        filaExcel: fila.filaExcel,
        codigo: fila.codigo,
        estado: 'sin_datos' as const,
        mensaje: 'Sin costo, precio neto ni precio mínimo en el Excel',
      };
    }

    if (noMatchPorFila.has(fila.filaExcel)) {
      return {
        filaExcel: fila.filaExcel,
        codigo: fila.codigo,
        costoNuevo: fila.costoExcel ?? null,
        precioNetoNuevo: fila.precioNetoExcel ?? null,
        precioMinimoNuevo: fila.precioMinimoExcel ?? null,
        estado: 'no_encontrado' as const,
        mensaje: 'Artículo no encontrado según la importación',
      };
    }

    const producto = cache.get(fila.codigo) ?? null;

    const costoActual = producto ? numOrNull(producto.costo) : null;
    const precioNetoActual = producto ? numOrNull(producto.precioVenta) : null;
    const precioMinimoActual = producto ? numOrNull(producto.precioMinimo) : null;
    const precioNetoNuevo =
      fila.precioNetoExcel ?? (producto ? resolverPrecioNetoNuevo(fila, producto) : fila.costoExcel);
    const precioFinalActual =
      producto && precioNetoActual != null ? calcPrecioFinal(precioNetoActual, producto) : null;
    const precioFinalNuevo =
      producto && precioNetoNuevo != null && typeof precioNetoNuevo === 'number'
        ? calcPrecioFinal(precioNetoNuevo, producto)
        : null;

    const costoNuevo = fila.costoExcel ?? costoActual;
    const precioNetoNuevoFinal = typeof precioNetoNuevo === 'number' ? precioNetoNuevo : null;
    const precioMinimoNuevo = fila.precioMinimoExcel ?? precioMinimoActual;

    return {
      filaExcel: fila.filaExcel,
      codigo: fila.codigo,
      descripcion: producto ? String(producto.descripcion ?? '') : undefined,
      costoActual,
      costoNuevo,
      precioNetoActual,
      precioNetoNuevo: precioNetoNuevoFinal,
      precioFinalActual,
      precioFinalNuevo,
      precioMinimoActual,
      precioMinimoNuevo,
      estado: 'actualizar' as const,
      mensaje: producto ? undefined : 'Se actualizará (sin datos actuales del catálogo en la vista previa)',
      costoNuevoOriginal: costoNuevo,
      precioNetoNuevoOriginal: precioNetoNuevoFinal,
      precioMinimoNuevoOriginal: precioMinimoNuevo,
      productoMeta: producto ? metaDesdeProducto(producto) : null,
      editado: false,
      precioNetoManual: fila.precioNetoExcel != null,
    };
  });
}

export async function buildPreciosImportPreviewProveedor(
  filas: ExcelFilaPreciosImport[],
  resultadoApi?: PreciosProveedorImportResult | null
): Promise<PreciosImportPreviewRow[]> {
  const erroresPorFila = buildErroresPorFila(resultadoApi?.erroresFila ?? []);
  const noMatchPorFila = buildNoMatchPorFila(resultadoApi?.sinCoincidenciaDetalle ?? [], 'proveedor');

  return filas.map((fila) => {
    const errorMsg = erroresPorFila.get(fila.filaExcel);
    if (errorMsg) {
      return {
        filaExcel: fila.filaExcel,
        codigo: fila.codigo,
        estado: 'invalido' as const,
        mensaje: errorMsg,
      };
    }

    if (!fila.codigo) {
      return {
        filaExcel: fila.filaExcel,
        codigo: '',
        estado: 'invalido' as const,
        mensaje: 'Código de artículo del proveedor vacío',
      };
    }

    if (fila.costoExcel === null || fila.costoExcel === undefined) {
      return {
        filaExcel: fila.filaExcel,
        codigo: fila.codigo,
        estado: 'sin_datos' as const,
        mensaje: 'Sin costo en el Excel',
      };
    }

    if (noMatchPorFila.has(fila.filaExcel)) {
      return {
        filaExcel: fila.filaExcel,
        codigo: fila.codigo,
        costoNuevo: fila.costoExcel,
        estado: 'no_encontrado' as const,
        mensaje: 'Código de artículo del proveedor no encontrado en CPP',
      };
    }

    return {
      filaExcel: fila.filaExcel,
      codigo: fila.codigo,
      costoNuevo: fila.costoExcel,
      estado: 'actualizar' as const,
      mensaje: 'El precio de venta se recalculará según el % de ganancia del artículo',
      costoNuevoOriginal: fila.costoExcel,
      editado: false,
    };
  });
}

export function resumirPreciosImportPreview(filas: PreciosImportPreviewRow[]) {
  return {
    total: filas.length,
    actualizar: filas.filter((f) => f.estado === 'actualizar').length,
    noEncontrado: filas.filter((f) => f.estado === 'no_encontrado').length,
    invalido: filas.filter((f) => f.estado === 'invalido').length,
    sinDatos: filas.filter((f) => f.estado === 'sin_datos').length,
    editados: filas.filter((f) => f.editado).length,
  };
}

function valoresDistintos(a: number | null | undefined, b: number | null | undefined): boolean {
  if (a == null && b == null) return false;
  if (a == null || b == null) return true;
  return Math.abs(a - b) > 0.0001;
}

function marcarEditado(fila: PreciosImportPreviewRow): boolean {
  return (
    valoresDistintos(fila.costoNuevo, fila.costoNuevoOriginal) ||
    valoresDistintos(fila.precioNetoNuevo, fila.precioNetoNuevoOriginal) ||
    valoresDistintos(fila.precioMinimoNuevo, fila.precioMinimoNuevoOriginal)
  );
}

export function aplicarEdicionCostoArticulo(
  fila: PreciosImportPreviewRow,
  costo: number | null
): PreciosImportPreviewRow {
  if (fila.estado !== 'actualizar') return fila;

  const costoRedondeado = costo != null ? roundPrecioImport(costo) : null;
  const meta = fila.productoMeta;
  let precioNetoNuevo = fila.precioNetoNuevo ?? null;
  if (!fila.precioNetoManual && meta && costoRedondeado != null) {
    precioNetoNuevo = estimarPrecioNetoDesdeCostoMeta(costoRedondeado, meta);
  }
  const precioFinalNuevo =
    meta && precioNetoNuevo != null ? calcPrecioFinalMeta(precioNetoNuevo, meta) : null;

  const actualizada: PreciosImportPreviewRow = {
    ...fila,
    costoNuevo: costoRedondeado,
    precioNetoNuevo,
    precioFinalNuevo,
  };
  return { ...actualizada, editado: marcarEditado(actualizada) };
}

export function aplicarEdicionPrecioNetoArticulo(
  fila: PreciosImportPreviewRow,
  precioNeto: number | null
): PreciosImportPreviewRow {
  if (fila.estado !== 'actualizar') return fila;

  const precioRedondeado = precioNeto != null ? roundPrecioImport(precioNeto) : null;
  const meta = fila.productoMeta;
  const precioFinalNuevo =
    meta && precioRedondeado != null ? calcPrecioFinalMeta(precioRedondeado, meta) : null;

  const actualizada: PreciosImportPreviewRow = {
    ...fila,
    precioNetoNuevo: precioRedondeado,
    precioFinalNuevo,
    precioNetoManual: precioRedondeado != null,
  };
  return { ...actualizada, editado: marcarEditado(actualizada) };
}

export function aplicarEdicionCostoProveedor(
  fila: PreciosImportPreviewRow,
  costo: number | null
): PreciosImportPreviewRow {
  if (fila.estado !== 'actualizar') return fila;

  const costoRedondeado = costo != null ? roundPrecioImport(costo) : null;
  const actualizada: PreciosImportPreviewRow = {
    ...fila,
    costoNuevo: costoRedondeado,
  };
  return { ...actualizada, editado: marcarEditado(actualizada) };
}

